// Capa de acceso a la API.
//
// La cookie de sesion es httpOnly y SameSite=lax, asi que no se toca desde
// JavaScript: el navegador la manda sola. `credentials: 'same-origin'` es lo
// que hace que viaje, y en desarrollo el proxy de Vite mantiene el mismo
// origen (ver vite.config.ts).

export class ErrorAPI extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  // El Content-Type se declara SOLO si efectivamente va un cuerpo JSON.
  //
  // Fastify rechaza un POST con `Content-Type: application/json` y cuerpo
  // vacio: responde 400 FST_ERR_CTP_EMPTY_JSON_BODY. Declararlo siempre
  // rompia todos los POST sin cuerpo —logout, cerrar, reconciliar,
  // desactivar— y como el error no se mostraba, el boton simplemente no
  // hacia nada.
  const headers = new Headers(opciones.headers);
  if (opciones.body !== undefined && !(opciones.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const r = await fetch(ruta, { ...opciones, credentials: 'same-origin', headers });

  if (r.status === 204) return undefined as T;

  if (!r.ok) {
    // La API siempre manda { error } con un texto escrito para el operador.
    let mensaje = `Error ${r.status}`;
    try {
      const cuerpo = await r.json();
      if (cuerpo?.error) mensaje = cuerpo.error;
    } catch { /* respuesta sin JSON: queda el mensaje genérico */ }
    throw new ErrorAPI(mensaje, r.status);
  }

  return r.json() as Promise<T>;
}

export const api = {
  get:  <T>(ruta: string) => pedir<T>(ruta),
  post: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { method: 'POST', body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo) }),
  subir: <T>(ruta: string, datos: FormData) =>
    pedir<T>(ruta, { method: 'POST', body: datos }),
  borrar: (ruta: string) => pedir<void>(ruta, { method: 'DELETE' }),
};

/** Descarga un archivo que la API devuelve como adjunto. */
export async function descargar(ruta: string): Promise<void> {
  const r = await fetch(ruta, { credentials: 'same-origin' });
  if (!r.ok) {
    let mensaje = `Error ${r.status}`;
    try { mensaje = (await r.json())?.error ?? mensaje; } catch { /* sin JSON */ }
    throw new ErrorAPI(mensaje, r.status);
  }

  // El nombre viene en content-disposition, codificado en UTF-8 porque lleva
  // acentos y espacios.
  const cd = r.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const nombre = m?.[1] ? decodeURIComponent(m[1]) : 'descarga.xlsx';

  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
